import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { calendarService, ICalendarFilter, ICalendarLesson, IGetCalendarFilterOptionsResponse,
    IGetDayCalendarResponse, TLessonAction, TLessonStatus } from "../../../../entities/calendar";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { CalendarLessonActions } from "../../../../features/calendarLessonActions";
import { CalendarLessonCard } from "../../../../features/calendarLessonCard";
import { CalendarLessonCreate } from "../../../../features/calendarLessonCreate";
import { CalendarLessonHistory } from "../../../../features/calendarLessonHistory";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useMediaQuery } from "../../../../shared/lib/hooks/useMediaQuery";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { formatTime, getDaySlots, getLessonTimeRange, getSlotDateEnd, getVisibleRange } from "../../lib/date";
import { getCalendarLessonLayout, getCalendarSlotsByIndex, getPositionedLessons } from "../../lib/lessonLayout";
import { ISelectedPeriod } from "../../lib/types";
import { useCalendarDaySelection } from "../../lib/useCalendarDaySelection";
import { CalendarDaySlots } from "../slots/CalendarDaySlots";
import classes from '../calendarDay.module.scss'

interface IProps {
    date: Date;
    filter: ICalendarFilter;
    filterOptions: IGetCalendarFilterOptionsResponse;
    omitUserIds?: boolean;
    lessonId?: string;
    backTitle?: string;
    onBack?: () => void;
    onSelectLesson?: (lessonId: string) => void;
    onCloseLesson?: () => void;
    onContentOpen?: () => void;
    onContentChange?: (isOpen: boolean) => void;
}

const getLessonStatusClass = (status: TLessonStatus): string => {
    if(status === 'LESSON_STATUS_COMPLETED') return classes.completedLesson
    if(status === 'LESSON_STATUS_CANCELLED') return classes.cancelledLesson
    if(status === 'LESSON_STATUS_RESCHEDULED') return classes.rescheduledLesson

    return classes.scheduledLesson
}

export const CalendarDay: FC<IProps> = ({
    date, filter, filterOptions, lessonId,
    backTitle = 'Месяц', onBack, onSelectLesson, onCloseLesson, onContentOpen, onContentChange, omitUserIds = false,
}) => {

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [calendarDay, setCalendarDay] = useState<IGetDayCalendarResponse | null>(null)
    const [selectedPeriod, setSelectedPeriod] = useState<ISelectedPeriod | null>(null)
    const [reloadKey, setReloadKey] = useState<number>(0)
    const [lessonAction, setLessonAction] = useState<TLessonAction | null>(null)
    const [isLessonHistoryOpen, setIsLessonHistoryOpen] = useState<boolean>(false)
    const [lessonDetails, setLessonDetails] = useState<ICalendarLesson | null>(null)
    const [isLessonDetailsLoading, setIsLessonDetailsLoading] = useState<boolean>(false)
    const [lessonDetailsError, setLessonDetailsError] = useState<string>('')

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useMediaQuery('(max-width: 600px)')
    const isCompactTime = useMediaQuery('(max-width: 400px)')
    const displayTime = useCallback((value: Date): string => {
        const formattedTime = formatTime(value)

        return isCompactTime ? formattedTime.replace(/:00$/, '') : formattedTime
    }, [isCompactTime])

    const backToMonth = () => {
        if(onBack){
            onBack()
            return
        }

        navigate(
            {pathname: '..', search: location.search},
            {relative: 'route'}
        )
    }

    const slots = useMemo(() => getDaySlots(date), [date])
    const selectedLessonId = lessonId ?? selectedPeriod?.lesson?.id
    const hasOpenContent = Boolean(selectedPeriod || selectedLessonId)

    useEffect(() => {
        onContentChange?.(hasOpenContent)
    }, [hasOpenContent, onContentChange])

    useEffect(() => {
        return () => onContentChange?.(false)
    }, [onContentChange])

    useEffect(() => {
        let isActual = true

        const getData = async () => {
            try{
                setIsLoading(true)
                setCalendarDay(null)
                setSelectedPeriod(null)
                setLessonAction(null)
                setIsLessonHistoryOpen(false)

                const {userIds: _userIds, ...filterWithoutUserIds} = filter
                const response = await calendarService.getDayCalendar({
                    date: {
                        year: date.getFullYear(),
                        month: date.getMonth() + 1,
                        day: date.getDate(),
                    },
                    filter: omitUserIds ? filterWithoutUserIds : filter,
                })

                if(isActual) setCalendarDay(response)
            }
            catch(error){
                if(!isActual) return

                console.log(error)
                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении календаря на день', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [date, filter, omitUserIds, reloadKey, setGlobalMessage, setIsAuth])

    useEffect(() => {
        if(!selectedLessonId){
            setLessonDetails(null)
            setLessonDetailsError('')
            setIsLessonDetailsLoading(false)
            return
        }

        let isActual = true

        const getLesson = async () => {
            try{
                setIsLessonDetailsLoading(true)
                setLessonDetails(null)
                setLessonDetailsError('')

                const response = await calendarService.getLesson(selectedLessonId)

                if(isActual) setLessonDetails(response.lesson)
            }
            catch(error){
                if(!isActual) return

                console.log(error)
                const message = error instanceof MyError
                    ? error.message
                    : 'Ошибка при получении занятия'

                setLessonDetailsError(message)

                if(error instanceof AuthError){
                    if(error.status === 403){
                        setGlobalMessage({message: error.message || 'Нет доступа к занятию', type: 'error'})

                        if(onBack){
                            onBack()
                        }
                        else{
                            navigate(
                                {pathname: '..', search: location.search},
                                {relative: 'route', replace: true}
                            )
                        }

                        return
                    }

                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message, type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLessonDetailsLoading(false)
            }
        }

        getLesson()

        return () => {
            isActual = false
        }
    }, [location.search, navigate, onBack, selectedLessonId, setGlobalMessage, setIsAuth])

    const title = date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })

    const calendarSlotsByIndex = useMemo(() => {
        return getCalendarSlotsByIndex(calendarDay?.slots ?? [], date)
    }, [calendarDay, date])

    const positionedLessons = useMemo(() => {
        return getPositionedLessons(calendarDay?.slots ?? [], date)
    }, [calendarDay, date])

    const {visibleLessons, groupedLessons} = useMemo(() => {
        return getCalendarLessonLayout(positionedLessons, isMobile)
    }, [isMobile, positionedLessons])

    const getVisibleLessonTimeRange = useCallback((lesson: ICalendarLesson): string => {
        const visibleRange = getVisibleRange(date, lesson.startsAt, lesson.durationSlots)

        if(!visibleRange) return getLessonTimeRange(lesson, displayTime)

        return `${displayTime(visibleRange.visibleStartsAt)}–${displayTime(visibleRange.visibleEndsAt)}`
    }, [date, displayTime])

    const selectLesson = useCallback((lesson: ICalendarLesson) => {
        if(onSelectLesson){
            onSelectLesson(lesson.id)
            return
        }

        setSelectedPeriod({
            startsAt: new Date(lesson.startsAt),
            durationSlots: lesson.durationSlots,
            lesson,
        })
    }, [onSelectLesson])

    const closeLesson = () => {
        setSelectedPeriod(null)
        onCloseLesson?.()
    }

    const {
        slotsRef,
        activeSelection,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        cancelSelection,
    } = useCalendarDaySelection({
        slots,
        calendarSlotsByIndex,
        onSelectPeriod: setSelectedPeriod,
    })

    if(selectedPeriod && !selectedPeriod.lesson){
        return (
            <CalendarLessonCreate
                startsAt={selectedPeriod.startsAt}
                durationSlots={selectedPeriod.durationSlots}
                filter={filter}
                filterOptions={filterOptions}
                formatTime={formatTime}
                onCancel={() => setSelectedPeriod(null)}
                onMount={onContentOpen}
                onCreated={() => {
                    setSelectedPeriod(null)
                    setReloadKey(value => value + 1)
                }}
            />
        )
    }

    if(selectedLessonId){
        if(isLessonDetailsLoading || !lessonDetails){
            return (
                <section className={classes.container}>
                    <section className={classes.header}>
                        <button type="button" className={classes.backButton} onClick={closeLesson}>
                            <span aria-hidden="true">‹</span>
                            К расписанию
                        </button>
                    </section>
                    {isLessonDetailsLoading ? (
                        <section className={classes.loader}>
                            <LoaderSpinner />
                        </section>
                    ) : (
                        <section className={classes.detailsError}>{lessonDetailsError || 'Не удалось загрузить занятие'}</section>
                    )}
                </section>
            )
        }

        const lessonStartsAt = new Date(lessonDetails.startsAt)
        const lessonEndsAt = getSlotDateEnd(lessonStartsAt, lessonDetails.durationSlots)

        return (
            <>
                <CalendarLessonCard
                    lesson={lessonDetails}
                    startsAt={lessonStartsAt}
                    endsAt={lessonEndsAt}
                    formatTime={formatTime}
                    onBack={closeLesson}
                    onMount={onContentOpen}
                    onAction={action => {
                        if(
                            action === 'LESSON_ACTION_VIEW_HISTORY'
                        ){
                            setIsLessonHistoryOpen(true)
                            return
                        }

                        if(
                            action === 'LESSON_ACTION_COMPLETE' ||
                            action === 'LESSON_ACTION_CANCEL' ||
                            action === 'LESSON_ACTION_RESCHEDULE' ||
                            action === 'LESSON_ACTION_REOPEN_CANCELLED' ||
                            action === 'LESSON_ACTION_DELETE'
                        ) setLessonAction(action)
                    }}
                />
                <CalendarLessonHistory
                    open={isLessonHistoryOpen}
                    setOpen={setIsLessonHistoryOpen}
                    lessonId={lessonDetails.id}
                />
                {lessonAction && (
                    <CalendarLessonActions
                        open={Boolean(lessonAction)}
                        setOpen={open => {
                            if(!open) setLessonAction(null)
                        }}
                        lesson={lessonDetails}
                        action={lessonAction}
                        onSuccess={() => {
                            setLessonAction(null)
                            closeLesson()
                            setReloadKey(value => value + 1)
                        }}
                    />
                )}
            </>
        )
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <button type="button" className={classes.backButton} onClick={backToMonth}>
                    <span aria-hidden="true">‹</span>
                    {backTitle}
                </button>
                <h2 className={classes.title}>{title}</h2>
            </section>
            {isLoading && (
                <section className={classes.loader}>
                    <LoaderSpinner />
                </section>
            )}

            {!isLoading && calendarDay && (
                <CalendarDaySlots
                    slotsRef={slotsRef}
                    slots={slots}
                    calendarSlotsByIndex={calendarSlotsByIndex}
                    activeSelection={activeSelection}
                    visibleLessons={visibleLessons}
                    groupedLessons={groupedLessons}
                    compactTime={isCompactTime}
                    formatTime={displayTime}
                    getLessonTimeRange={getVisibleLessonTimeRange}
                    getLessonStatusClass={getLessonStatusClass}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={cancelSelection}
                    onSelectLesson={selectLesson}
                />
            )}
        </section>
    )
}
